import { Injectable } from 'framework/di';

import { DomHelper } from '../../helpers/dom.helper';
import { AppService } from '../app/app.service';
import { FilterAPI } from './filter.api';
import {
  COLLECTION_ALL_STORAGE_KEY,
  FILTER_DESKTOP_WRAPPER_SELECTOR,
  FILTER_MOBILE_WRAPPER_SELECTOR,
  FILTER_OPTION_DISPLAY_TYPE,
  FILTER_OPTION_TYPE,
  FILTER_TAG_KEY,
  PRE_ACTION,
  PRE_REQUEST_IDS,
} from './filter.constants';
import { FilterHelper } from './filter.helper';
import { FilterAPIAdditionParams, FilterAPIResponse } from './filter.model';
import { FilterStorageHelper } from './filter.storage.helper';
import { FilterStore } from './filter.store';
import { FilterTranslationService } from './filter.translation.service';
import { FilterUrlHelper } from './filter.url.helper';

const DOM_UPDATE_DELAY_MS = 500;
/**
 * Core business logic service for filter widget operations.
 *
 * Orchestrates filter API calls, state management, URL handling, pagination,
 * and filter option processing. Coordinates between FilterAPI, FilterStore,
 * and various helper services to provide complete filter functionality.
 *
 * @remarks
 * This service handles:
 * - Filter API requests with additional parameters
 * - Pagination state and URL synchronization
 * - Collection path parsing and storage
 * - Filter tree data enrichment
 * - SEO meta tag management
 * - Request ID tracking for analytics
 *
 * Technical Support teams can extend this service to customize filter behavior,
 * API request parameters, pagination logic, or add shop-specific business rules.
 *
 * @example
 * Extend to add custom API parameters:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterService', (FilterService) => {
 *   return class CustomFilterService extends FilterService {
 *     async fetchFilters(addition = {}) {
 *       // Add shop-specific parameters
 *       const customAddition = {
 *         ...addition,
 *         shopId: this.getShopId(),
 *         customSegment: this.getCustomerSegment()
 *       };
 *       return super.fetchFilters(customAddition);
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override pagination behavior:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterService', (FilterService) => {
 *   return class extends FilterService {
 *     async processPagination(newPage, behavior = 'replace') {
 *       // Add custom analytics before pagination
 *       this.trackPaginationEvent(newPage, behavior);
 *       return super.processPagination(newPage, behavior);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterService {
  /**
   * Creates a new FilterService instance.
   *
   * @param appService - Application service for accessing configuration
   * @param filterAPI - API service for filter requests
   * @param filterStore - State store for filter data
   * @param filterHelper - Helper utilities for filter operations
   * @param storageHelper - LocalStorage helper for persistence
   * @param urlHelper - URL manipulation and query parameter helper
   * @param filterTranslation - Translation service for i18n
   * @param domHelper - DOM helper for accessibility announcements
   */
  constructor(
    private appService: AppService,
    private filterAPI: FilterAPI,
    private filterStore: FilterStore,
    private filterHelper: FilterHelper,
    private storageHelper: FilterStorageHelper,
    private urlHelper: FilterUrlHelper,
    private filterTranslation: FilterTranslationService,
    private domHelper: DomHelper
  ) {}

  /**
   * Initializes URL shorten maps from app configuration.
   *
   * Loads shortened URL parameter mappings from app configuration and stores
   * them in FilterStore. This allows using shorter query parameters in URLs
   * for better SEO and readability (e.g., 'c' instead of 'pf_c_color').
   *
   * @remarks
   * Should be called during app initialization before any filter operations.
   * The shorten maps are used by URL helper to encode/decode parameters.
   *
   * @example
   * Override to add custom URL shortening rules:
   * ```typescript
   * initShortenMaps() {
   *   super.initShortenMaps();
   *   // Add custom shop-specific URL shortenings
   *   this.filterStore.addShortenMapping('custom_filter', 'cf');
   * }
   * ```
   */
  initShortenMaps(): void {
    const shortenUrlParamList = this.appService.TAEAppConfig.filterSettings.shortenUrlParamList;

    if (Array.isArray(shortenUrlParamList)) {
      this.filterStore.initShortenMaps(shortenUrlParamList);
    }
  }

  /**
   * Fetches filter results from the API with optional additional parameters.
   *
   * Main method for requesting filtered product data. Handles API calls,
   * request ID tracking, and action type storage for analytics. Supports
   * various addition parameters like pagination behavior and collection focus.
   *
   * @param addition - Additional API request parameters
   * @param addition.behavior - Fetch behavior ('more' | 'previous' | 'replace')
   * @param addition.inCollectionSearchFocusing - Focus on collection search results
   * @returns Promise resolving to API response or error string
   *
   * @remarks
   * Automatically saves request ID and action type to localStorage for analytics
   * tracking. The request ID is used to correlate user actions with API requests.
   *
   * @example
   * Override to add shop-specific request headers:
   * ```typescript
   * async fetchFilters(addition = {}) {
   *   // Add custom parameters for shop
   *   const customAddition = {
   *     ...addition,
   *     shopMetadata: {
   *       segment: this.getCustomerSegment(),
   *       locale: this.getCurrentLocale()
   *     }
   *   };
   *   return super.fetchFilters(customAddition);
   * }
   * ```
   */
  async fetchFilters(
    addition: FilterAPIAdditionParams = {}
  ): Promise<string | FilterAPIResponse | null> {
    const url = this.appService.TAEAppConfig.filterUrl;

    // Announce loading for all users (screen readers)
    this.domHelper.setFilterBusyState(true, 'Loading products');

    const result = await this.filterAPI.fetchAPI(url, {
      additionParams: {
        ...addition,
      },
    });

    // Announce loading completed
    this.domHelper.setFilterBusyState(false);

    // Process response if it's a FilterAPIResponse
    if (result && typeof result !== 'string' && 'meta' in result) {
      const requestId = result.meta?.rid;
      const action = this.filterHelper.isSearchPage() ? 'search' : 'filter';

      this.saveRequestId(action, requestId, result.bundles);
      this.setActionLocalStorage(action);

      // Delay to ensure DOM is updated before reading product count
      setTimeout(() => {
        // Get product count from DOM data attribute after DOM update
        let productCount = 0;
        const productListElement = document.querySelector('.boost-sd__product-list');
        if (productListElement) {
          const dataProductCount = productListElement.getAttribute('data-total-product');
          productCount = dataProductCount ? parseInt(dataProductCount, 10) : 0;
        }

        // Check if this was a clear action
        const isClearing = this.filterStore.getState('isClearing') || false;
        const message = isClearing
          ? `Clear applied. ${productCount} products found`
          : `Filter applied. ${productCount} products found`;

        // Announce filter/clear applied with product count for all users (accessibility)
        this.domHelper.announceToScreenReader(message, 'assertive', 3000);

        // Reset clear flag
        if (isClearing) {
          this.filterStore.setState({ isClearing: false });
        }
      }, DOM_UPDATE_DELAY_MS);

      // Clear keyboard state if it was set
      this.filterStore.setState({ filterKeyboardActive: false });
    }
    return result;
  }

  /**
   * Resets all filters and pagination to initial state.
   *
   * Clears pagination to page 1, removes page parameter from URL, and
   * fetches fresh filter results. Used when clearing all filters or
   * starting a new filter session.
   *
   * @returns Promise resolving to API response or error string
   *
   * @remarks
   * Does not clear filter selections, only resets pagination state.
   * Use clearAllParamHistory() to clear filter selections.
   *
   * @example
   * Override to add custom reset logic:
   * ```typescript
   * async resetFilters() {
   *   // Clear custom shop state before reset
   *   this.clearCustomShopFilters();
   *   const result = await super.resetFilters();
   *   // Track filter reset event
   *   this.analytics.track('filters_reset');
   *   return result;
   * }
   * ```
   */
  async resetFilters(): Promise<string | FilterAPIResponse | null> {
    const pagingationValue = this.filterStore.state.value.pagination;

    this.filterStore.setState({ pagination: { ...pagingationValue, page: 1 } });

    const { isShortenUrlParam } = this.appService.filterSettings;

    this.filterHelper.deleteQueryParamHistory('page', {
      preventPushHistory: true,
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });
    this.filterHelper.setPaginationSession(1);

    return await this.fetchFilters();
  }

  /**
   * Synchronizes active URL filter parameters to FilterStore context.
   *
   * Parses current URL parameters and updates selectedFilter state to
   * match. Ensures filter UI reflects the actual URL state, especially
   * important after browser back/forward navigation.
   *
   * @remarks
   * Called during initialization and after URL changes to keep state
   * synchronized with URL parameters.
   *
   * @example
   * Override to add custom filter synchronization:
   * ```typescript
   * async syncActiveFiltersToContext() {
   *   await super.syncActiveFiltersToContext();
   *   // Sync custom shop-specific filters
   *   this.syncCustomShopFilters();
   * }
   * ```
   */
  async syncActiveFiltersToContext() {
    const { paramList } = this.filterHelper.getParamsHistory();
    const listFilters: any[] = [];
    paramList.forEach((param) => {
      param.values.forEach((value) => {
        listFilters.push({ data: { key: param.key, value } });
      });
    });
    this.filterStore.setState({
      selectedFilter: listFilters,
    });
  }

  /**
   * Parses URL history parameters and updates filter state.
   *
   * Extracts filter parameters, sort options, and pagination from URL,
   * updates FilterStore state, and rewrites URL with proper formatting.
   * Handles URL shortening if enabled in settings.
   *
   * @remarks
   * Called during filter initialization to restore filter state from URL.
   * Ensures consistent URL format and state synchronization.
   *
   * @example
   * Override to customize URL parameter parsing:
   * ```typescript
   * parseHistoryParams() {
   *   super.parseHistoryParams();
   *   // Parse custom shop-specific URL parameters
   *   const customParams = this.parseCustomShopParams();
   *   this.filterStore.setState({ customParams });
   * }
   * ```
   */
  parseHistoryParams() {
    const { paramList, sort, pagination } = this.filterHelper.getParamsHistory();

    const { isShortenUrlParam } = this.appService.filterSettings;

    if (sort) {
      this.filterStore.setState({ sort });
    }
    this.filterStore.setState({
      pagination: {
        ...this.filterStore.state.value.pagination,
        page: pagination.page ? parseInt(pagination.page) : 1,
      },
    });

    this.syncActiveFiltersToContext();
    this.filterHelper.rewriteUrlFromParams(
      { paramList, sort, pagination },
      { isShortenUrlParam, urlScheme: this.filterHelper.getUrlScheme() }
    );
  }

  /**
   * Checks if current page exceeds total pages and fetches filters if needed
   * Called during initialization to handle edge cases with pagination
   *
   * @returns Promise that resolves when check/fetch is complete
   */
  async handleExceededPage(): Promise<void> {
    const productList = this.filterStore
      .getState('document')
      .querySelector('.boost-sd__product-list');

    if (productList) {
      const totalPage = Number(productList.getAttribute('data-total-page'));
      const pageParam = Number(this.filterStore.state.value.defaultParams.page) || 1;

      if (pageParam > totalPage) {
        await this.fetchFilters();
      }
    }
  }

  /**
   * Updates pagination state and fetches new page of results.
   *
   * Handles page navigation, URL parameter updates, and triggers API fetch
   * with the specified behavior. Updates session storage for persistence.
   *
   * @param newPage - The new page number to navigate to
   * @param behavior - Fetch behavior determining how results are displayed:
   *   - 'replace': Replace current results (default)
   *   - 'more': Append to existing results (infinite scroll)
   *   - 'previous': Prepend to existing results
   * @returns Promise that resolves when pagination is complete
   *
   * @remarks
   * Automatically updates URL with new page parameter and stores page
   * in session storage. Handles URL shortening if enabled.
   *
   * @example
   * Override to add custom pagination tracking:
   * ```typescript
   * async processPagination(newPage, behavior = 'replace') {
   *   // Track pagination for analytics
   *   this.analytics.track('page_changed', {
   *     page: newPage,
   *     behavior,
   *     filterCount: this.getActiveFilterCount()
   *   });
   *   return super.processPagination(newPage, behavior);
   * }
   * ```
   */
  async processPagination(
    newPage: number,
    behavior: 'more' | 'previous' | 'replace' = 'replace'
  ): Promise<void> {
    const prevPage = this.filterStore.state.value.pagination.page;

    if (prevPage === newPage) {
      return;
    }

    this.filterStore.setState({
      pagination: {
        ...this.filterStore.state.value.pagination,
        page: newPage,
      },
    });

    const { isShortenUrlParam } = this.appService.filterSettings;

    this.filterHelper.setQueryParamHistory('page', `${newPage}`, false, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });

    this.filterHelper.setPaginationSession(newPage);

    await this.fetchFilters({ behavior });
  }

  /**
   * Saves collection ID to localStorage for "all collections" tracking.
   *
   * Stores the collection ID associated with the special "all" collection
   * handle. This enables proper filtering when users navigate between
   * specific collections and the "all collections" view.
   *
   * @remarks
   * Only saves when on a collection page with handle='all' and the ID
   * has changed. Used to maintain collection context across page loads.
   *
   * @example
   * Override to add custom collection tracking:
   * ```typescript
   * savingCollectionId() {
   *   super.savingCollectionId();
   *   // Track collection views for analytics
   *   const { collection_id, collection_handle } = this.appService.TAEAppConfig.generalSettings;
   *   if (collection_handle === 'all') {
   *     this.analytics.track('all_collection_viewed', { id: collection_id });
   *   }
   * }
   * ```
   */
  savingCollectionId(): void {
    const { collection_id, collection_handle } = this.appService.TAEAppConfig.generalSettings || {};
    // save Id collection All to localStorage
    const getIdCollectionAll = this.storageHelper.getLocalStorage(COLLECTION_ALL_STORAGE_KEY);
    if (collection_handle === 'all' && getIdCollectionAll !== `${collection_id}`) {
      this.storageHelper.setLocalStorage(COLLECTION_ALL_STORAGE_KEY, `${collection_id}`);
    }
  }

  /**
   * Saves request ID to localStorage for analytics tracking.
   *
   * Stores API request IDs associated with filter, search, and suggest
   * operations. These IDs are used to correlate user actions (clicks, add-to-cart)
   * with specific API requests for analytics and conversion tracking.
   *
   * @param type - Type of request ('filter', 'search', 'suggest')
   * @param requestId - The request ID from API response
   * @param bundles - Optional bundles array for widget-specific tracking
   *
   * @remarks
   * Request IDs are stored with keys like 'filter', 'search', or
   * '{widgetId}_{placement}' for widget-specific tracking. Used by
   * analytics services to attribute conversions to filter usage.
   *
   * @example
   * Override to add custom request tracking:
   * ```typescript
   * saveRequestId(type, requestId, bundles = []) {
   *   super.saveRequestId(type, requestId, bundles);
   *   // Send request ID to custom analytics
   *   this.customAnalytics.trackRequest({
   *     type,
   *     requestId,
   *     timestamp: Date.now(),
   *     userSegment: this.getUserSegment()
   *   });
   * }
   * ```
   */
  saveRequestId(
    type: 'filter' | 'search' | 'suggest',
    requestId?: string,
    bundles: unknown[] = []
  ): void {
    if (!requestId) return;

    const stored = this.storageHelper.getLocalStorage(PRE_REQUEST_IDS);
    const requestIds: Record<string, string> = stored ? JSON.parse(stored) : {};
    requestIds[type] = requestId;

    if (bundles && Array.isArray(bundles)) {
      bundles.forEach((bundle: unknown) => {
        if (typeof bundle === 'object' && bundle !== null && 'widgetId' in bundle) {
          const widgetId = (bundle as { widgetId: string }).widgetId;
          const placement =
            type === 'search'
              ? 'search_page'
              : type === 'suggest'
                ? 'instant_search'
                : 'product_page';
          requestIds[`${widgetId}_${placement}`] = requestId;
        }
      });
    }

    this.storageHelper.setLocalStorage(PRE_REQUEST_IDS, JSON.stringify(requestIds));
  }

  /**
   * Sets the action type in localStorage for analytics tracking.
   *
   * Stores the most recent action type (filter, search, suggest) to
   * localStorage. Used by analytics to determine the source of user
   * interactions with products.
   *
   * @param action - The action type ('filter', 'search', 'suggest')
   *
   * @remarks
   * This value is read by product click handlers and add-to-cart events
   * to attribute conversions to the correct source.
   *
   * @example
   * Override to add custom action tracking:
   * ```typescript
   * setActionLocalStorage(action) {
   *   super.setActionLocalStorage(action);
   *   // Track action changes for funnel analysis
   *   this.analytics.track('user_action_changed', {
   *     previousAction: this.getPreviousAction(),
   *     newAction: action
   *   });
   * }
   * ```
   */
  setActionLocalStorage(action: 'filter' | 'search' | 'suggest'): void {
    this.storageHelper.setLocalStorage(PRE_ACTION, action);
  }

  /**
   * Sets up SEO meta tags based on filter state.
   *
   * Adds noindex meta tag when filters are active to prevent duplicate
   * content issues. Helps maintain SEO health by preventing search engines
   * from indexing filtered product pages.
   *
   * @remarks
   * Only adds the meta tag if:
   * - enableRobot setting is true (default)
   * - No existing noindex tag is present
   * - Filter parameters exist in URL OR search query exists on non-search page
   *
   * @example
   * Override to customize SEO tag behavior:
   * ```typescript
   * setupSEOMetaTags() {
   *   super.setupSEOMetaTags();
   *   // Add custom canonical URL for filtered pages
   *   if (this.hasActiveFilters()) {
   *     this.setCanonicalUrl(this.getBaseCollectionUrl());
   *   }
   * }
   * ```
   */
  setupSEOMetaTags(): void {
    const robot = document.querySelector('meta[content="noindex,nofollow,nosnippet"]');

    const { enableRobot = true } = this.appService.TAEAppConfig.generalSettings || {};
    const { isShortenUrlParam } = this.appService.filterSettings;

    if (
      enableRobot &&
      !robot &&
      (this.urlHelper.checkExistFilterOptionParam(isShortenUrlParam) ||
        (this.urlHelper.getQueryParamByKey('q') && !this.filterHelper.isSearchPage()))
    ) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex,nofollow,nosnippet';

      document.head.append(meta);
    }
  }

  /**
   * Gets current collection handle from URL.
   *
   * Extracts the collection handle from the URL pathname by parsing
   * the /collections/{handle} pattern. Returns undefined if not on
   * a collection page.
   *
   * @returns Collection handle string if on collection page, undefined otherwise
   *
   * @remarks
   * Only extracts handle from standard /collections/handle URLs.
   * Does not handle collection+tag URLs (e.g., /collections/handle/tag).
   *
   * @example
   * Override to support custom collection URL patterns:
   * ```typescript
   * getCurrentCollection() {
   *   // Try standard pattern first
   *   const standard = super.getCurrentCollection();
   *   if (standard) return standard;
   *
   *   // Try custom shop pattern (e.g., /shop/collection-name)
   *   const customMatch = window.location.pathname.match(/\/shop\/([^/?#]+)/);
   *   return customMatch?.[1];
   * }
   * ```
   */
  getCurrentCollection(): string | undefined {
    // Get URL path safely
    const pathname = window.location.pathname;

    // Check for collection paths
    if (pathname.includes('/collections/')) {
      // Extract collection handle: /collections/summer-sale -> summer-sale
      const collectionRegex = /\/collections\/([^/?#]+)/;
      const match = pathname.match(collectionRegex);

      if (match && match[1]) {
        // For standard collection pages, return the collection handle
        return match[1];
      }
    }
  }

  /**
   * Parses collection path from URL and updates app configuration.
   *
   * Extracts collection handle and optional tag from URL path, retrieves
   * collection ID from storage, and updates TAEAppConfig with collection
   * context. Handles both /collections/handle and /collections/handle/tag URLs.
   *
   * @remarks
   * Updates generalSettings with:
   * - collection_id: Retrieved from storage by handle
   * - collection_handle: Extracted from URL
   * - collection_tags: Array with tag if present in URL, undefined otherwise
   *
   * @example
   * Override to handle custom collection URL structures:
   * ```typescript
   * parseCollectionPath() {
   *   const pathname = window.location.pathname;
   *   if (pathname.startsWith('/custom-collections/')) {
   *     // Handle custom URL structure
   *     const [, , handle, tag] = pathname.split('/');
   *     this.setCollectionContext(handle, tag);
   *   } else {
   *     super.parseCollectionPath();
   *   }
   * }
   * ```
   */
  parseCollectionPath(): void {
    const pathname = window.location.pathname;

    // Use regex pattern consistent with getCurrentCollection() method
    // Handles locale prefixes (e.g., /de/collections/summer) and avoids false matches
    const collectionRegex = /\/collections\/([^/?#]+)(?:\/([^/?#]+))?/;
    const match = pathname.match(collectionRegex);

    if (match && match[1]) {
      const collectionHandle = match[1];
      this.appService.TAEAppConfig.generalSettings.collection_id = parseInt(
        String(this.storageHelper.getCollectionIdByHandleKey(collectionHandle))
      );
      this.appService.TAEAppConfig.generalSettings.collection_handle = collectionHandle;

      // Check for collection tag (second capture group)
      if (match[2]) {
        const collectionTag = this.storageHelper.getCollectionTagFromSlugKey(match[2]);
        if (collectionTag) {
          this.appService.TAEAppConfig.generalSettings.collection_tags = [collectionTag];
        } else {
          this.appService.TAEAppConfig.generalSettings.collection_tags = undefined;
        }
      } else {
        this.appService.TAEAppConfig.generalSettings.collection_tags = undefined;
      }
    }
  }

  /**
   * Clears all filter parameters from URL history.
   *
   * Removes all filter-related query parameters from the URL without
   * affecting other parameters. Supports URL shortening and optional
   * history push prevention.
   *
   * @param options - Configuration options
   * @param options.preventPushHistory - If true, updates URL without adding to browser history
   * @param options.isShortenUrlParam - If true, uses shortened parameter names
   *
   * @remarks
   * Delegates to urlHelper.clearAllParamHistory(). Does not affect
   * FilterStore state - only clears URL parameters.
   *
   * @example
   * Override to add custom parameter clearing:
   * ```typescript
   * clearAllParamHistory(options = {}) {
   *   // Clear custom shop parameters first
   *   this.clearCustomShopParams();
   *   super.clearAllParamHistory(options);
   *   // Track filter clear event
   *   this.analytics.track('all_filters_cleared');
   * }
   * ```
   */
  clearAllParamHistory(
    options: { preventPushHistory?: boolean; isShortenUrlParam?: boolean } = {}
  ): void {
    this.urlHelper.clearAllParamHistory(options);
  }

  /**
   * Saves collection information during initialization.
   *
   * Stores the mapping between collection handle and collection ID in
   * localStorage. This enables retrieving collection ID from handle
   * when parsing URLs or processing collection filters.
   *
   * @remarks
   * Only saves on collection pages (page === 'collection') and when
   * collection_handle is present. The mapping persists across sessions
   * for fast collection ID lookup.
   *
   * @example
   * Override to add custom collection tracking:
   * ```typescript
   * saveCollectionInfo() {
   *   super.saveCollectionInfo();
   *   const { collection_id, collection_handle } = this.appService.TAEAppConfig.generalSettings;
   *   if (collection_handle) {
   *     // Track collection view for recommendations
   *     this.trackCollectionView(collection_id, collection_handle);
   *   }
   * }
   * ```
   */
  saveCollectionInfo(): void {
    const { collection_id, collection_handle, page } =
      this.appService.TAEAppConfig.generalSettings || {};

    if (page === 'collection' && collection_handle) {
      this.storageHelper.saveCollectionIdByHandle(collection_handle, `${collection_id}`);
    }
  }

  /**
   * Enriches filter tree with complete option data and metadata.
   *
   * Takes available filter options and selected filters, updates option
   * values based on settings, and fills missing metadata for selected
   * filters. Returns a complete filter tree with display labels,
   * dependencies, and type information.
   *
   * @param filterOptions - Available filter options from API
   * @param selectedFilters - Currently selected filters (may have incomplete data)
   * @param settingOptionValues - Settings controlling option display
   * @param settingOptionValues.showOutOfStockOption - Whether to show out-of-stock options
   * @param settingOptionValues.hideSingleOption - Whether to hide filters with only one value
   * @returns Enriched selected filters with full metadata for rendering
   *
   * @remarks
   * This method coordinates updateValuesOptions() and fillMissValueForFilterTree()
   * to create a complete filter tree ready for rendering.
   *
   * @example
   * Override to customize filter tree processing:
   * ```typescript
   * fillFilterTree(filterOptions, selectedFilters, settingOptionValues) {
   *   const enriched = super.fillFilterTree(filterOptions, selectedFilters, settingOptionValues);
   *   // Add custom shop-specific filter metadata
   *   return enriched.map(filter => ({
   *     ...filter,
   *     customMetadata: this.getCustomFilterMetadata(filter.data.key)
   *   }));
   * }
   * ```
   */
  fillFilterTree(
    filterOptions: any[],
    selectedFilters: any[],
    settingOptionValues: { showOutOfStockOption?: boolean; hideSingleOption?: boolean }
  ): any[] {
    const options = this.updateValuesOptions(filterOptions, settingOptionValues);
    return this.fillMissValueForFilterTree(options, selectedFilters);
  }

  /**
   * Updates option values based on filter settings.
   *
   * Processes filter options to filter by doc_count, create labels,
   * handle single-option hiding, apply sorting, format rating values,
   * and set appropriate select types. This is the main processing
   * method for transforming raw API options into display-ready options.
   *
   * @param options - Filter options to update
   * @param settingOptionValues - Settings object controlling option processing
   * @param settingOptionValues.showOutOfStockOption - Whether to show options with 0 products
   * @param settingOptionValues.hideSingleOption - Whether to hide options with only 1 value
   * @returns Updated options array with processed values
   *
   * @remarks
   * Applies multiple transformations:
   * - Filters values by doc_count (unless showOutOfStockOption or keepValuesStatic)
   * - Creates missing labels from keys
   * - Hides single-value options if hideSingleOption enabled
   * - Removes empty options from DOM
   * - Applies sortType (key-asc, key-desc, etc.)
   * - Formats rating labels with star text
   * - Sets multiple select type for certain filter types
   *
   * @example
   * Override to add custom option processing:
   * ```typescript
   * updateValuesOptions(options, settingOptionValues) {
   *   const updated = super.updateValuesOptions(options, settingOptionValues);
   *   // Add custom processing for shop-specific filters
   *   return updated.map(option => {
   *     if (option.filterType === 'vendor') {
   *       // Add vendor logos to values
   *       option.values = option.values.map(v => ({
   *         ...v,
   *         logo: this.getVendorLogo(v.key)
   *       }));
   *     }
   *     return option;
   *   });
   * }
   * ```
   */
  updateValuesOptions(options: any[], settingOptionValues: any): any[] {
    if (!Array.isArray(options)) return options;
    const { hideSingleOption = false, showOutOfStockOption = false } = settingOptionValues;

    return options.map((option: any) => {
      // Option Values is array
      if (Array.isArray(option.values)) {
        const isMultiLevel =
          option.displayType === 'multi_level_collections' ||
          option.filterType === 'multi_level_tag';

        // Filter doc_count valid when showOutOfStockOption = false and option.keepValuesStatic = false
        if (!showOutOfStockOption && !option.keepValuesStatic) {
          option.values = isMultiLevel
            ? this.filterMultiLevelOptionsByDocCount(option.values)
            : this.filterOptionsByDocCount(option.values);
        }

        // Create labels in values if not exists
        const labelAvailable = option.values.some((value: any) => 'label' in value);
        if (!labelAvailable) {
          option.values.forEach((value: any) => {
            if (value.key) value.label = value.key;
          });
        }

        // Hide filter option when filter settings hideSingleOption = true and length values = 1
        if (hideSingleOption && option.values.length === 1) {
          const onlyNode = option.values[0];
          if (isMultiLevel) {
            if (!this.hasNestedChildren(onlyNode)) {
              option.values = [];
            }
          } else {
            option.values = [];
          }
        }
      }

      // Remove empty filter options from DOM
      if (option?.values?.length === 0) {
        const filterOption = document.getElementById(
          `${option.filterOptionId.replace(/::/g, '__')}-toggle`
        );

        filterOption && filterOption?.closest('.boost-sd__filter-option')?.remove();
      }

      // if API return not value sortType then check and assign default value sortType if this Option valid
      if (!option.sortType) {
        option.sortType = this.isDefaultSort(option) ? 'key-asc' : '';
      }

      const isManualValues = option.manualValues?.length > 0;
      /* filter option settings - advanced Sort type
       * sort <=> isManualValues & sortManualValues or allValues(!isManualValues)
       */
      if (
        ((option.sortManualValues && isManualValues) || !isManualValues) &&
        Array.isArray(option.values) &&
        option.sortType
      ) {
        const [key, order] = option.sortType?.split('-') || [];
        if (key && order) {
          let _key = key;
          if (option.filterType === 'collection' && key === 'key') {
            _key = 'displayName';
          }

          option.values = this.filterHelper.sortByKey(option.values, _key, order);
        }
      }

      if (option.filterType === 'review_ratings' || option.displayType === 'rating') {
        // only apply for rating
        option.values = option.values?.map((item) => {
          item.key = Math.round(item.from);
          item.label = this.formatRatingValue(item.from, option.showExactRating);
          return item;
        });
      }

      // some filterType not have selectType but default multiple selectType
      if (
        option.displayType === 'list' &&
        ['stock', 'percent_sale', 'price', 'variants_price'].includes(option.filterType)
      ) {
        option.selectType = 'multiple';
      }

      return option;
    });
  }

  /**
   * Fills missing values for filter tree from available options
   * Enriches selected filters with display labels, metadata, and dependencies
   *
   * @param options - Available filter options with full data
   * @param selectedFilters - Currently selected filters (may have incomplete data)
   * @returns Enriched filter tree with complete metadata
   */
  private fillMissValueForFilterTree(options: any[], selectedFilters: any[]): any[] {
    const optionsMap = options.reduce((obj, val) => ({ ...obj, [val.filterOptionId]: val }), {});
    let fillTagDependencies = false;

    const filterTree = selectedFilters.map((selectedFilter) => {
      let { valueDisplay } = selectedFilter.data;
      const { key, value } = selectedFilter.data;

      if (selectedFilter.type) {
        // Filter already has data, do not need to fill
        return selectedFilter;
      }

      if (key === FILTER_TAG_KEY) {
        fillTagDependencies = true;
        return {
          data: { label: 'Collection Tag', value, key, valueDisplay: value },
          metaData: { key, value },
          type: 'tag',
        };
      }

      if (optionsMap[key]?.displayType === 'range') {
        return this.generateFilterDataForRange(key, value, optionsMap[key]);
      }

      const filterOption = optionsMap[key];
      if (!filterOption) {
        // Option not found, return as-is
        return selectedFilter;
      }

      // Fix: BC-1033 - Incorrect refine by element label of multi_level_tag
      if (filterOption.filterType === 'multi_level_tag' && !valueDisplay) {
        const keyDisplayName = this.flattenTagsToObject(filterOption.values);
        valueDisplay = keyDisplayName[value];
      }

      if (filterOption.filterType === 'multi_level_tag') {
        filterOption.valueDisplay = valueDisplay;
      } else {
        // filterOption.values[0].label use to check this filter have label to display
        if (filterOption.values && filterOption.values.length > 0 && filterOption.values[0].label) {
          const matchValue = filterOption.values.find((v) => String(v.key) === String(value));
          if (matchValue) {
            let { label } = matchValue;
            const { prefix } = filterOption;

            // If have prefix then remove prefix from label
            if (prefix && typeof label === 'string') {
              const cleanPrefix = prefix.replace(/\\/g, '');
              label = label.replace(cleanPrefix, '');
            }

            filterOption.valueDisplay = label;
          } else {
            filterOption.valueDisplay = valueDisplay;
          }
        }
      }

      return this.generateFilterDataForList(key, value, filterOption);
    });

    const collectionFilterOptionId = this.getCollectionFilterOptionId(options);

    if (fillTagDependencies) {
      const tag = filterTree.find((f) => f.data.key === FILTER_TAG_KEY);
      const collection = filterTree.find((f) => f.data.key === collectionFilterOptionId);

      if (tag && collection) {
        const tagMetadata = { ...tag.metaData };
        tag.metaData.dependencies = [{ ...collection.metaData }];
        collection.metaData.dependencies = [tagMetadata];
      }
    }

    return filterTree;
  }

  /**
   * Filters options by doc_count (single level)
   */
  private filterOptionsByDocCount(values: any[]): any[] {
    return values.filter((value: any) => (value.doc_count ?? 0) > 0);
  }

  /**
   * Filters multi-level options by doc_count recursively
   */
  private filterMultiLevelOptionsByDocCount(values: any[]): any[] {
    return values.filter((value: any) => {
      if (value.tags && Array.isArray(value.tags)) {
        value.tags = this.filterMultiLevelOptionsByDocCount(value.tags);
        return value.tags.length > 0 || (value.doc_count ?? 0) > 0;
      }
      if (value.subTags && Array.isArray(value.subTags)) {
        value.subTags = this.filterMultiLevelOptionsByDocCount(value.subTags);
        return value.subTags.length > 0 || (value.doc_count ?? 0) > 0;
      }
      return (value.doc_count ?? 0) > 0;
    });
  }

  /**
   * Checks if a node has nested children (tags or subTags)
   */
  private hasNestedChildren(node: any): boolean {
    if (node.tags && Array.isArray(node.tags) && node.tags.length > 0) {
      return true;
    }
    if (node.subTags && Array.isArray(node.subTags) && node.subTags.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Flattens nested tag structure into a flat object mapping tag to displayName
   *
   * @param array - Array of tag objects with potential subTags
   * @returns Object mapping tag keys to display names
   */
  private flattenTagsToObject(array: any[]): Record<string, string> {
    const result: Record<string, string> = {};

    array.forEach((item) => {
      result[item.tag] = item.displayName;
      if (item.subTags) {
        Object.assign(result, this.flattenTagsToObject(item.subTags));
      }
    });

    return result;
  }

  /**
   * Gets the collection filter option ID from options array
   */
  getCollectionFilterOptionId(options: any[]): string {
    if (options?.length > 0) {
      return options.filter((option) => option.filterType === 'collection')?.[0]?.filterOptionId;
    }

    return 'pf_c_collection';
  }

  /**
   * Determines if an option should have default sorting
   */
  private isDefaultSort(option: any): boolean {
    const alwaySortFilterType = [
      FILTER_OPTION_TYPE.REVIEW_RATINGS,
      FILTER_OPTION_TYPE.PERCENT_SALE,
    ];

    if (alwaySortFilterType.includes(option.filterType)) return true;

    const excludeFilterType = [FILTER_OPTION_TYPE.STOCK];
    const excludeDisplayType = [
      FILTER_OPTION_DISPLAY_TYPE.RANGE,
      FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS,
    ];

    const isSortAll = option.valueType === 'all';
    const isSortManual = option.valueType !== 'all' && option.sortManualValues;
    const isTextRangeSlider =
      option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE &&
      option.isNumberRangeSlider === false;

    return (
      !excludeFilterType.includes(option.filterType) &&
      (!excludeDisplayType.includes(option.displayType) || isTextRangeSlider) &&
      (isSortAll || isSortManual)
    );
  }

  /**
   * Formats rating value for display with appropriate star text
   */
  private formatRatingValue(from: number, showExactRating: boolean): string {
    let label = from.toFixed();
    if (from === 1) {
      label += ` ${this.filterTranslation.translateByKey('ratingStar', 'star')}`;
    } else {
      label += ` ${this.filterTranslation.translateByKey('ratingStars', 'stars')}`;
    }

    if (!showExactRating) label += ` ${this.filterTranslation.translateByKey('ratingUp', '& Up')}`;

    return label;
  }

  /**
   * Formats price label with currency
   */
  formatPriceLabel(to: number, from: number, precision?: number): string {
    const formatValue = (val: number) => {
      return precision ? val.toFixed(precision) : val.toString();
    };

    if (to && from) {
      return `${formatValue(from)} - ${formatValue(to)}`;
    } else if (to) {
      return `Under ${formatValue(to)}`;
    } else {
      return `Over ${formatValue(from)}`;
    }
  }

  /**
   * Formats percent sale label
   */
  formatPercentSaleLabel(from: number, to: number): string {
    if (to && from) {
      return `${from}% - ${to}%`;
    } else if (to) {
      return `Up to ${to}%`;
    } else {
      return `${from}% and above`;
    }
  }

  /**
   * Generates filter data object for list filters
   */
  generateFilterDataForList(key: string, value: string, filterOption: any): any {
    if (['variants_price', 'price'].includes(filterOption.filterType)) {
      // Handle price filters
      if (filterOption.displayType === 'range') {
        return this.generateFilterDataForRange(key, value, filterOption);
      }
      const [from, to] = value.split(':');
      filterOption.valueDisplay = this.formatPriceLabel(
        to ? Number(to) : 0,
        from ? Number(from) : 0
      );
    }

    if (filterOption.filterType === 'percent_sale') {
      // Handle percent sale filters
      if (filterOption.displayType === 'range') {
        return this.generateFilterDataForRange(key, value, filterOption);
      }
      const [from, to] = value.split(':');
      filterOption.valueDisplay = this.formatPercentSaleLabel(
        from ? Number(from) : 0,
        to ? Number(to) : 0
      );
    }

    return {
      data: {
        label: filterOption.label,
        value,
        key,
        valueDisplay: filterOption.valueDisplay || value,
        selectType: filterOption.selectType,
      },
      metaData: { key, value, type: filterOption.displayType },
      type: filterOption.displayType,
    };
  }

  /**
   * Generates filter data structure for range-type filters
   */
  private generateFilterDataForRange(key: string, value: any, option: any): any {
    return {
      data: {
        key,
        value: this.filterHelper.toRangeValue(value),
        label: option.label,
        moneyFormatValue: option?.moneyFormatValue || '',
      },
      metaData: {
        key,
        value: this.filterHelper.toStringValue(value),
        type: option.displayType || 'range',
      },
      type: option.displayType || 'range',
    };
  }
  getFilterTreeParent(_dom?: HTMLElement) {
    const isMobileSize = this.filterHelper.isMobile(
      parseInt(
        (this.appService.TAEAppConfig.generalSettings as { breakpointtabletportraitmax?: string })
          ?.breakpointtabletportraitmax || '768',
        10
      )
    );
    const document = _dom || this.filterStore.getState('document');
    if (isMobileSize) {
      return document.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR);
    }

    return document.querySelector(FILTER_DESKTOP_WRAPPER_SELECTOR);
  }
}
